/**
 * 图集媒体缓存服务
 * 用于缓存图集中的图片和视频资源
 * 缓存键格式：{用户id}_{歌曲id}_album-media{序号}
 * 存储到 music-cache-db 数据库的 music-album 对象存储中
 */

import { isAliyunAlbumVideo, isAliyunCdnUrl } from '@/utils/aliyunHlsPlayback.js';

function inferAlbumBlobType(contentType, firstBytes) {
  const header = contentType?.split(';')[0]?.trim();
  if (header && header !== 'application/octet-stream') {
    return header;
  }
  const bytes = firstBytes instanceof Uint8Array ? firstBytes : null;
  const b0 = bytes?.[0];
  const b1 = bytes?.[1];
  if (b0 === 0xFF && b1 === 0xD8) return 'image/jpeg';
  if (b0 === 0x89 && b1 === 0x50) return 'image/png';
  if (b0 === 0x47 && b1 === 0x49) return 'image/gif';
  if (bytes?.length >= 8) {
    const brand = String.fromCharCode(bytes[4], bytes[5], bytes[6], bytes[7]);
    if (brand === 'ftyp') return 'video/mp4';
  }
  if (b0 === 0x52 && b1 === 0x49) return 'image/webp';
  return 'image/jpeg';
}

/** iOS Safari 从 IndexedDB 读出的 Blob 经常无 type / 无法直接给 img 解码，需 slice 成新 Blob */
async function reviveIndexedDbBlob(stored, fallbackType = 'image/jpeg') {
  if (!stored) return null;
  if (stored instanceof ArrayBuffer) {
    const type = inferAlbumBlobType('', new Uint8Array(stored.slice(0, 16)));
    return new Blob([stored], { type: type || fallbackType });
  }
  if (!(stored instanceof Blob) || stored.size <= 0) {
    if (stored?.blob instanceof Blob) {
      return reviveIndexedDbBlob(stored.blob, fallbackType);
    }
    return null;
  }
  let type = stored.type;
  if (!type) {
    try {
      const head = new Uint8Array(await stored.slice(0, 16).arrayBuffer());
      type = inferAlbumBlobType('', head) || fallbackType;
    } catch {
      type = fallbackType;
    }
  }
  try {
    return stored.slice(0, stored.size, type);
  } catch {
    return new Blob([stored], { type });
  }
}

const DB_NAME = 'music-cache-db';
const DB_VERSION = 9; // 🔥 升级到 9：新增 audio-ranges / audio-metadata（音频 Range 缓存）
const ALBUM_MEDIA_STORE = 'music-album';
const ALBUM_META_CACHE_PREFIX = 'album-meta-v1';
const ALBUM_META_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

class AlbumMediaCacheService {
  constructor() {
    this.db = null;
    this.isInitialized = false;
    this.initPromise = null;
    // 🔥 新增：缓存已创建的 Blob URL，避免重复创建导致视频重新加载
    this.blobUrlCache = new Map(); // key: cacheKey, value: blobUrl
    this.albumMetaMemoryCache = new Map(); // key: cacheKey, value: { cachedAt, mediaItems }
    /** 同一 cacheKey 并发 getMediaUrl 去重，避免并行 createObjectURL */
    this.inFlightUrlRequests = new Map();
    this.initDB();
  }

  /**
   * 初始化IndexedDB数据库
   */
  async initDB() {
    // 如果已经在初始化中，返回现有的Promise
    if (this.initPromise) {
      return this.initPromise;
    }
    
    this.initPromise = this._doInitDB();
    return this.initPromise;
  }
  
  async _doInitDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      
      request.onupgradeneeded = (event) => {
        const db = request.result;
        console.log('🖼️ [图集媒体缓存] 数据库升级中，版本:', event.oldVersion, '->', event.newVersion);
        
        // 确保 music-album 对象存储存在
        if (!db.objectStoreNames.contains(ALBUM_MEDIA_STORE)) {
          db.createObjectStore(ALBUM_MEDIA_STORE);
          console.log('🖼️ [图集媒体缓存] 创建 music-album 对象存储');
        } else {
          console.log('🖼️ [图集媒体缓存] music-album 对象存储已存在');
        }
        
        // 确保其他必要的对象存储也存在（兼容性）
        if (!db.objectStoreNames.contains('music-files')) {
          db.createObjectStore('music-files');
        }
        if (!db.objectStoreNames.contains('meta')) {
          db.createObjectStore('meta');
        }
        if (!db.objectStoreNames.contains('video-files')) {
          db.createObjectStore('video-files');
        }
        if (!db.objectStoreNames.contains('partial-video-cache')) {
          db.createObjectStore('partial-video-cache');
        }
        if (!db.objectStoreNames.contains('music-lrc')) {
          db.createObjectStore('music-lrc');
          console.log('🖼️ [图集媒体缓存] 创建 music-lrc 对象存储');
        }

        // 🔥 新增（v8）：HLS 分片缓存与分片清单对象存储
        if (!db.objectStoreNames.contains('video-segments')) {
          db.createObjectStore('video-segments');
          console.log('🖼️ [图集媒体缓存] 创建 video-segments 对象存储');
        }
        if (!db.objectStoreNames.contains('video-seg-manifest')) {
          db.createObjectStore('video-seg-manifest');
          console.log('🖼️ [图集媒体缓存] 创建 video-seg-manifest 对象存储');
        }
        
        // 🔥 新增（v9）：音频 Range 缓存（in-line key）
        if (!db.objectStoreNames.contains('audio-ranges')) {
          const rs = db.createObjectStore('audio-ranges', { keyPath: 'key' });
          rs.createIndex('audioId', 'audioId', { unique: false });
          rs.createIndex('cachedAt', 'cachedAt', { unique: false });
          console.log('🖼️ [图集媒体缓存] 创建 audio-ranges 对象存储');
        }
        if (!db.objectStoreNames.contains('audio-metadata')) {
          db.createObjectStore('audio-metadata', { keyPath: 'audioId' });
          console.log('🖼️ [图集媒体缓存] 创建 audio-metadata 对象存储');
        }
        
        console.log('🖼️ [图集媒体缓存] 数据库升级完成，当前对象存储:', Array.from(db.objectStoreNames));
      };
      
      request.onsuccess = (event) => {
        this.db = event.target.result;
        this.isInitialized = true;
        console.log('🖼️ [图集媒体缓存] IndexedDB初始化成功');
        resolve(this.db);
      };
      
      request.onerror = (event) => {
        console.error('❌ [图集媒体缓存] IndexedDB初始化失败:', event.target.error);
        this.isInitialized = false;
        reject(event.target.error);
      };
    });
  }
  
  /**
   * 确保数据库已初始化
   */
  async ensureDBReady() {
    if (!this.isInitialized) {
      await this.initDB();
    }
    return this.db;
  }

  /**
   * 生成缓存键
   * @param {string|number} userId - 用户ID
   * @param {string|number} songId - 歌曲ID
   * @param {number} index - 媒体在图集中的序号（从0开始）
   * @returns {string} 缓存键
   */
  generateCacheKey(userId, songId, index) {
    return `${userId}_${songId}_album-media${index}`;
  }

  generateAlbumMetaCacheKey(mediaAlbumId) {
    return `${ALBUM_META_CACHE_PREFIX}_${mediaAlbumId}`;
  }

  cloneAlbumMetaItems(mediaItems) {
    if (!Array.isArray(mediaItems)) {
      return [];
    }
    return mediaItems.map(item => ({ ...item }));
  }

  isAlbumMetaRecordValid(record, ttlMs = ALBUM_META_CACHE_TTL_MS) {
    if (!record || !Array.isArray(record.mediaItems) || typeof record.cachedAt !== 'number') {
      return false;
    }
    return (Date.now() - record.cachedAt) <= ttlMs;
  }

  async getCachedAlbumMeta(mediaAlbumId, options = {}) {
    if (mediaAlbumId === null || mediaAlbumId === undefined) {
      return null;
    }
    const ttlMs = typeof options.ttlMs === 'number' ? options.ttlMs : ALBUM_META_CACHE_TTL_MS;
    const cacheKey = this.generateAlbumMetaCacheKey(mediaAlbumId);

    const memoryRecord = this.albumMetaMemoryCache.get(cacheKey);
    if (this.isAlbumMetaRecordValid(memoryRecord, ttlMs)) {
      console.log(`🖼️ [图集媒体缓存] 图集元数据内存缓存命中: ${cacheKey}`);
      return this.cloneAlbumMetaItems(memoryRecord.mediaItems);
    }

    try {
      await this.ensureDBReady();

      return new Promise((resolve) => {
        try {
          const tx = this.db.transaction([ALBUM_MEDIA_STORE], 'readonly');
          const store = tx.objectStore(ALBUM_MEDIA_STORE);
          const request = store.get(cacheKey);

          request.onsuccess = () => {
            const record = request.result;
            if (!this.isAlbumMetaRecordValid(record, ttlMs)) {
              console.log(`🖼️ [图集媒体缓存] 图集元数据缓存未命中或已过期: ${cacheKey}`);
              resolve(null);
              return;
            }
            this.albumMetaMemoryCache.set(cacheKey, record);
            console.log(`🖼️ [图集媒体缓存] 图集元数据 IndexedDB 缓存命中: ${cacheKey}`);
            resolve(this.cloneAlbumMetaItems(record.mediaItems));
          };

          request.onerror = () => {
            console.warn(`⚠️ [图集媒体缓存] 读取图集元数据缓存失败: ${cacheKey}`);
            resolve(null);
          };
        } catch (error) {
          console.warn(`⚠️ [图集媒体缓存] 创建图集元数据读取事务失败: ${cacheKey}`, error);
          resolve(null);
        }
      });
    } catch (error) {
      console.warn(`⚠️ [图集媒体缓存] 获取图集元数据缓存失败: ${cacheKey}`, error);
      return null;
    }
  }

  async cacheAlbumMeta(mediaAlbumId, mediaItems) {
    if (mediaAlbumId === null || mediaAlbumId === undefined || !Array.isArray(mediaItems)) {
      return;
    }
    const cacheKey = this.generateAlbumMetaCacheKey(mediaAlbumId);
    const record = {
      cachedAt: Date.now(),
      mediaItems: this.cloneAlbumMetaItems(mediaItems)
    };

    this.albumMetaMemoryCache.set(cacheKey, record);

    try {
      await this.ensureDBReady();
      await new Promise((resolve, reject) => {
        try {
          const tx = this.db.transaction([ALBUM_MEDIA_STORE], 'readwrite');
          const store = tx.objectStore(ALBUM_MEDIA_STORE);
          store.put(record, cacheKey);

          tx.oncomplete = () => resolve();
          tx.onerror = () => reject(tx.error);
        } catch (error) {
          reject(error);
        }
      });
      console.log(`🖼️ [图集媒体缓存] 图集元数据已写入缓存: ${cacheKey}, count=${mediaItems.length}`);
    } catch (error) {
      console.warn(`⚠️ [图集媒体缓存] 写入图集元数据缓存失败: ${cacheKey}`, error);
    }
  }

  /**
   * 获取缓存的媒体资源
   * @param {string|number} userId - 用户ID
   * @param {string|number} songId - 歌曲ID
   * @param {number} index - 媒体在图集中的序号
   * @returns {Promise<Blob|null>} 缓存的媒体资源，如果不存在则返回null
   */
  async getCachedMedia(userId, songId, index) {
    try {
      await this.ensureDBReady();
      
      const cacheKey = this.generateCacheKey(userId, songId, index);
      
      return new Promise((resolve) => {
        try {
          const tx = this.db.transaction([ALBUM_MEDIA_STORE], 'readonly');
          const store = tx.objectStore(ALBUM_MEDIA_STORE);
          const request = store.get(cacheKey);
          
          request.onsuccess = () => {
            const result = request.result;
            if (!result) {
              console.log(`🖼️ [图集媒体缓存] 缓存未命中: ${cacheKey}`);
              resolve(null);
              return;
            }
            reviveIndexedDbBlob(result)
              .then((revived) => {
                if (revived) {
                  console.log(`🖼️ [图集媒体缓存] 缓存命中: ${cacheKey}, type=${revived.type || 'unknown'}`);
                  resolve(revived);
                } else {
                  console.warn(`🖼️ [图集媒体缓存] 缓存记录无法解码为 Blob: ${cacheKey}`);
                  resolve(null);
                }
              })
              .catch(() => resolve(null));
          };
          
          request.onerror = () => {
            console.warn(`🖼️ [图集媒体缓存] 获取缓存失败: ${cacheKey}`);
            resolve(null);
          };
        } catch (error) {
          console.warn(`🖼️ [图集媒体缓存] 创建获取事务失败: ${cacheKey}`, error);
          resolve(null);
        }
      });
    } catch (error) {
      console.error('❌ [图集媒体缓存] 获取缓存失败:', error);
      return null;
    }
  }

  /**
   * 缓存媒体资源
   * @param {string|number} userId - 用户ID
   * @param {string|number} songId - 歌曲ID
   * @param {number} index - 媒体在图集中的序号
   * @param {Blob} blob - 媒体资源的Blob对象
   * @returns {Promise<void>}
   */
  async cacheMedia(userId, songId, index, blob) {
    try {
      await this.ensureDBReady();
      
      const cacheKey = this.generateCacheKey(userId, songId, index);
      const playableBlob = (await reviveIndexedDbBlob(blob)) || blob;
      
      return new Promise((resolve, reject) => {
        try {
          const tx = this.db.transaction([ALBUM_MEDIA_STORE], 'readwrite');
          const store = tx.objectStore(ALBUM_MEDIA_STORE);
          store.put(playableBlob, cacheKey);
          
           tx.oncomplete = () => {
             console.log(`🖼️ [图集媒体缓存] 缓存成功: ${cacheKey}, 大小: ${Math.round(playableBlob.size / 1024)}KB, type=${playableBlob.type || 'unknown'}`);
             
             // 🔥 修复：缓存成功后立即创建 Blob URL 并缓存到内存中
             // 这样 getMediaUrl 就能立即获取到 URL，不需要等待 IndexedDB 读取
             // 这样前2个媒体加载完成后就能立即启动媒体切换
             if (!this.blobUrlCache.has(cacheKey)) {
               const blobUrl = URL.createObjectURL(playableBlob);
               this.blobUrlCache.set(cacheKey, blobUrl);
               console.log(`🖼️ [图集媒体缓存] 已创建并缓存 Blob URL: 索引 ${index}`);
             }
             
             resolve();
           };
          
          tx.onerror = () => {
            console.warn(`🖼️ [图集媒体缓存] 缓存失败: ${cacheKey}`);
            reject(tx.error);
          };
        } catch (error) {
          console.warn(`🖼️ [图集媒体缓存] 创建存储事务失败: ${cacheKey}`, error);
          reject(error);
        }
      });
    } catch (error) {
      console.error('❌ [图集媒体缓存] 缓存失败:', error);
      throw error;
    }
  }

  /**
   * 下载并缓存媒体资源
   * @param {string} url - 媒体资源的URL
   * @param {string|number} userId - 用户ID
   * @param {string|number} songId - 歌曲ID
   * @param {number} index - 媒体在图集中的序号
   * @param {Object} options - 选项
   * @param {Function} options.progressCallback - 进度回调函数 (progress) => {}
   * @param {Object} options.headers - 请求头
   * @returns {Promise<Blob>} 下载的媒体资源
   */
  async downloadAndCacheMedia(url, userId, songId, index, options = {}) {
    try {
      const { progressCallback, headers = {} } = options;
      
      console.log(`🖼️ [图集媒体缓存] 开始下载媒体资源: ${url}, 索引: ${index}`);

      // 阿里云 CDN 直链：必须 no-referrer，且不能附加 Authorization（会破坏 OSS 签名 → 403）
      let response;
      if (isAliyunCdnUrl(url)) {
        response = await fetch(url, { referrerPolicy: 'no-referrer' });
      } else {
        const token = localStorage.getItem('accessToken');
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        response = await fetch(url, { headers });
      }
      
      if (!response.ok) {
        throw new Error(`下载失败: ${response.status} ${response.statusText}`);
      }
      
      const contentLength = response.headers.get('content-length');
      const total = contentLength ? parseInt(contentLength, 10) : 0;
      
      const reader = response.body.getReader();
      const chunks = [];
      let loaded = 0;
      
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;
        
        chunks.push(value);
        loaded += value.length;
        
        if (progressCallback && total > 0) {
          const progress = Math.round((loaded / total) * 100);
          progressCallback(progress, loaded, total);
        }
      }
      
      // 合并所有chunks为Blob；必须带 MIME，否则 iOS 上 img/video 会解不出画面
      const blobType = inferAlbumBlobType(
        response.headers.get('content-type'),
        chunks[0]
      );
      const blob = new Blob(chunks, { type: blobType });
      
      // 缓存资源
      await this.cacheMedia(userId, songId, index, blob);
      
      console.log(`🖼️ [图集媒体缓存] 下载并缓存完成: 索引 ${index}, 大小: ${Math.round(blob.size / 1024)}KB`);
      
      return blob;
    } catch (error) {
      console.error(`❌ [图集媒体缓存] 下载并缓存失败: 索引 ${index}`, error);
      throw error;
    }
  }

  /**
   * 预加载图集中的所有媒体资源（顺序加载策略）
   * - 网络加载：顺序加载，但提前准备下一个媒体（最多3个线程准备，不占用网速）
   * - 缓存加载：完全单线程顺序加载
   * @param {Array} mediaItems - 图集媒体项列表
   * @param {string|number} userId - 用户ID
   * @param {string|number} songId - 歌曲ID
   * @param {Object} options - 选项
   * @param {Function} options.progressCallback - 进度回调函数 (index, progress, total) => {}
   * @param {Function} options.statusCallback - 状态回调函数 (index, status) => {} status: 'loading' | 'completed' | 'failed'
   * @param {number} options.maxPrepareThreads - 最大准备线程数（用于网络加载提前准备），默认3
   * @param {number[]|null} options.indicesToLoad - 仅预加载指定索引；null 表示全部
   * @returns {Promise<Object>} 返回加载状态对象 { loadedCount, failedCount, loadedIndices: Set }
   */
  async preloadAllMedia(mediaItems, userId, songId, options = {}) {
    if (!mediaItems || mediaItems.length === 0) {
      console.log('🖼️ [图集媒体缓存] 没有需要预加载的媒体资源');
      return { loadedCount: 0, failedCount: 0, loadedIndices: new Set() };
    }
    
    const {
      progressCallback,
      statusCallback,
      maxPrepareThreads = 3,
      indicesToLoad = null
    } = options;

    const targetIndices = (indicesToLoad && indicesToLoad.length > 0)
      ? [...indicesToLoad].filter((i) => i >= 0 && i < mediaItems.length).sort((a, b) => a - b)
      : Array.from({ length: mediaItems.length }, (_, i) => i);

    if (targetIndices.length === 0) {
      return { loadedCount: 0, failedCount: 0, loadedIndices: new Set() };
    }
    
    console.log(`🖼️ [图集媒体缓存] 开始顺序预加载 ${targetIndices.length}/${mediaItems.length} 个媒体资源（网络加载最多 ${maxPrepareThreads} 个线程准备）`);
    
    let successCount = 0;
    let failCount = 0;
    const loadedIndices = new Set(); // 已加载完成的索引集合
    
    // 第一步：检查所有媒体的缓存状态，分类为缓存加载和网络加载
    const cacheStatusMap = new Map(); // index -> { needsNetwork: boolean, cached: boolean }
    
    console.log('🖼️ [图集媒体缓存] 第一步：检查目标媒体的缓存状态...');
    for (const i of targetIndices) {
      // 阿里云盘视频走 HLS 流式播放，不整文件下载到 IndexedDB
      if (isAliyunAlbumVideo(mediaItems[i])) {
        cacheStatusMap.set(i, { needsNetwork: false, cached: false, hls: true });
        loadedIndices.add(i);
        successCount++;
        if (progressCallback) {
          progressCallback(i, 100, mediaItems.length);
        }
        if (statusCallback) {
          statusCallback(i, 'completed');
        }
        console.log(`🖼️ [图集媒体缓存] 媒体 ${i} 为阿里云 HLS 视频，跳过整文件预加载`);
        continue;
      }

      const cachedBlob = await this.getCachedMedia(userId, songId, i);
      const needsNetwork = !cachedBlob;
      cacheStatusMap.set(i, { needsNetwork, cached: !!cachedBlob });
      
      if (cachedBlob) {
        console.log(`🖼️ [图集媒体缓存] 媒体 ${i} 已缓存，将从缓存加载`);
        loadedIndices.add(i);
        successCount++;
        if (progressCallback) {
          progressCallback(i, 100, mediaItems.length);
        }
        if (statusCallback) {
          statusCallback(i, 'completed');
        }
      } else {
        console.log(`🖼️ [图集媒体缓存] 媒体 ${i} 需要网络加载`);
      }
    }
    
    // 第二步：顺序加载需要网络下载的媒体
    const networkLoadIndices = Array.from(cacheStatusMap.entries())
      .filter(([index, status]) => status.needsNetwork)
      .map(([index]) => index);
    
    if (networkLoadIndices.length === 0) {
      console.log('🖼️ [图集媒体缓存] 所有媒体都已缓存，无需网络加载');
      return { loadedCount: successCount, failedCount: failCount, loadedIndices };
    }
    
    console.log(`🖼️ [图集媒体缓存] 第二步：开始顺序网络加载 ${networkLoadIndices.length} 个媒体（提前准备最多 ${maxPrepareThreads} 个）`);
    
    // 准备队列：用于提前准备下一个媒体（不占用网速，只是准备URL等）
    const prepareQueue = [];
    let currentLoadingIndex = -1; // 当前正在下载的媒体索引（占用网速）
    let prepareThreadCount = 0; // 当前准备线程数
    
    // 准备下一个媒体的函数（不占用网速）
    const prepareNextMedia = async (index) => {
      if (index >= mediaItems.length) return null;
      
      const mediaItem = mediaItems[index];
      if (!mediaItem) return null;
      
      // 获取媒体URL（不下载，只是准备）
      const mediaUrl = mediaItem.mediaType === 0 
        ? (mediaItem.pictureUrl || mediaItem.url)
        : (mediaItem.videoUrl || mediaItem.url);
      
      if (!mediaUrl) {
        console.warn(`🖼️ [图集媒体缓存] 媒体 ${index} 没有URL，跳过准备`);
        return null;
      }
      
      // 准备请求头等（不实际发起网络请求）
      const token = localStorage.getItem('accessToken');
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      console.log(`🖼️ [图集媒体缓存] 提前准备媒体 ${index} 的URL和请求头（不占用网速）`);
      return { index, mediaUrl, headers };
    };
    
    // 顺序加载网络媒体
    for (let i = 0; i < networkLoadIndices.length; i++) {
      const index = networkLoadIndices[i];
      const mediaItem = mediaItems[index];
      
      currentLoadingIndex = index;
      console.log(`🖼️ [图集媒体缓存] 开始下载媒体 ${index}（占用网速）`);
      
      if (statusCallback) {
        statusCallback(index, 'loading');
      }
      
      try {
        // 检查是否已在准备队列中准备好
        let preparedInfo = null;
        const preparedIndex = prepareQueue.findIndex(p => p && p.index === index);
        if (preparedIndex >= 0) {
          preparedInfo = prepareQueue[preparedIndex];
          prepareQueue.splice(preparedIndex, 1);
          prepareThreadCount--;
          console.log(`🖼️ [图集媒体缓存] 使用已准备的媒体 ${index} 信息`);
        } else {
          // 如果没有准备，现在准备（同步，不占用网速）
          preparedInfo = await prepareNextMedia(index);
        }
        
        if (!preparedInfo) {
          throw new Error('无法准备媒体URL');
        }
        
        // 开始实际下载（占用网速）
        await this.downloadAndCacheMedia(preparedInfo.mediaUrl, userId, songId, index, {
          headers: preparedInfo.headers,
          progressCallback: (progress) => {
            if (progressCallback) {
              progressCallback(index, progress, mediaItems.length);
            }
          }
        });
        
        loadedIndices.add(index);
        successCount++;
        console.log(`🖼️ [图集媒体缓存] 媒体 ${index} 下载完成`);
        
        if (statusCallback) {
          statusCallback(index, 'completed');
        }
      } catch (error) {
        failCount++;
        console.error(`❌ [图集媒体缓存] 媒体 ${index} 下载失败:`, error);
        
        if (progressCallback) {
          progressCallback(index, 100, mediaItems.length);
        }
        if (statusCallback) {
          statusCallback(index, 'failed');
        }
      }
      
      // 提前准备下一个媒体（最多 maxPrepareThreads 个线程）
      // 在当前媒体下载的同时，准备后续的媒体（不占用网速）
      const nextIndices = networkLoadIndices.slice(i + 1, i + 1 + maxPrepareThreads);
      for (let j = 0; j < nextIndices.length; j++) {
        const nextIndex = nextIndices[j];
        // 如果还没准备，且准备线程数未满，则开始准备
        const alreadyPreparing = prepareQueue.some(p => p && p.index === nextIndex);
        if (!alreadyPreparing && prepareThreadCount < maxPrepareThreads) {
          prepareThreadCount++;
          prepareNextMedia(nextIndex).then(preparedInfo => {
            if (preparedInfo) {
              prepareQueue.push(preparedInfo);
              console.log(`🖼️ [图集媒体缓存] 媒体 ${nextIndex} 准备完成（不占用网速），等待下载`);
            }
            prepareThreadCount--;
          }).catch(error => {
            console.warn(`⚠️ [图集媒体缓存] 媒体 ${nextIndex} 准备失败:`, error);
            prepareThreadCount--;
          });
        }
      }
    }
    
    console.log(`🖼️ [图集媒体缓存] 媒体资源预加载完成 (${targetIndices.length}/${mediaItems.length}): 成功 ${successCount} 个, 失败 ${failCount} 个`);
    return { loadedCount: successCount, failedCount: failCount, loadedIndices };
  }

  /**
   * 获取媒体资源的URL（优先从缓存读取）
   * @param {Object} mediaItem - 媒体项
   * @param {string|number} userId - 用户ID
   * @param {string|number} songId - 歌曲ID
   * @param {number} index - 媒体在图集中的序号
   * @returns {Promise<string>} 媒体资源的URL（Blob URL或原始URL）
   */
  async getMediaUrl(mediaItem, userId, songId, index) {
    const cacheKey = this.generateCacheKey(userId, songId, index);

    // 🔥 修复：先检查是否已有缓存的 Blob URL，避免重复创建
    if (this.blobUrlCache.has(cacheKey)) {
      const existingBlobUrl = this.blobUrlCache.get(cacheKey);
      if (existingBlobUrl && existingBlobUrl.startsWith('blob:')) {
        console.log(`🖼️ [图集媒体缓存] 复用已存在的 Blob URL: 索引 ${index}`);
        return existingBlobUrl;
      }
      this.blobUrlCache.delete(cacheKey);
    }

    if (this.inFlightUrlRequests.has(cacheKey)) {
      console.log(`🖼️ [图集媒体缓存] 复用进行中的 URL 请求: 索引 ${index}`);
      return this.inFlightUrlRequests.get(cacheKey);
    }

    const request = this._resolveMediaUrlFromCache(mediaItem, userId, songId, index, cacheKey)
      .finally(() => {
        this.inFlightUrlRequests.delete(cacheKey);
      });
    this.inFlightUrlRequests.set(cacheKey, request);
    return request;
  }

  async _resolveMediaUrlFromCache(mediaItem, userId, songId, index, cacheKey) {
    try {
      const cachedBlob = await this.getCachedMedia(userId, songId, index);

      if (cachedBlob) {
        const blobUrl = URL.createObjectURL(cachedBlob);
        this.blobUrlCache.set(cacheKey, blobUrl);
        console.log(`🖼️ [图集媒体缓存] 使用缓存资源并创建 Blob URL: 索引 ${index}, type=${cachedBlob.type || 'unknown'}, size=${cachedBlob.size}`);
        return blobUrl;
      }

      console.warn(`⚠️ [图集媒体缓存] 缓存未命中，索引 ${index}，不返回原始URL以避免触发OneDrive请求`);
      return null;
    } catch (error) {
      console.error(`❌ [图集媒体缓存] 获取媒体URL失败: 索引 ${index}`, error);
      return null;
    }
  }

  /**
   * 清除指定歌曲的所有图集媒体缓存
   * @param {string|number} userId - 用户ID
   * @param {string|number} songId - 歌曲ID
   * @returns {Promise<void>}
   */
  async clearSongCache(userId, songId) {
    try {
      await this.ensureDBReady();
      
      const prefix = `${userId}_${songId}_album-media`;
      
      // 🔥 新增：先清理对应的 Blob URL
      const blobUrlsToRevoke = [];
      this.blobUrlCache.forEach((blobUrl, cacheKey) => {
        if (cacheKey.startsWith(prefix)) {
          blobUrlsToRevoke.push(blobUrl);
          this.blobUrlCache.delete(cacheKey);
        }
      });
      
      // 释放所有相关的 Blob URL
      blobUrlsToRevoke.forEach(blobUrl => {
        try {
          URL.revokeObjectURL(blobUrl);
          console.log(`🧹 [图集媒体缓存] 释放 Blob URL: ${blobUrl.substring(0, 50)}...`);
        } catch (error) {
          console.warn(`⚠️ [图集媒体缓存] 释放 Blob URL 失败:`, error);
        }
      });
      
      return new Promise((resolve) => {
        try {
          const tx = this.db.transaction([ALBUM_MEDIA_STORE], 'readwrite');
          const store = tx.objectStore(ALBUM_MEDIA_STORE);
          
          // 获取所有键
          const request = store.getAllKeys();
          
          request.onsuccess = () => {
            const keys = request.result;
            const keysToDelete = keys.filter(key => key.startsWith(prefix));
            
            if (keysToDelete.length === 0) {
              console.log(`🖼️ [图集媒体缓存] 没有找到需要清除的缓存: ${prefix}`);
              resolve();
              return;
            }
            
            // 删除所有匹配的键
            let deletedCount = 0;
            keysToDelete.forEach(key => {
              const deleteRequest = store.delete(key);
              deleteRequest.onsuccess = () => {
                deletedCount++;
                if (deletedCount === keysToDelete.length) {
                  console.log(`🖼️ [图集媒体缓存] 清除缓存完成: ${deletedCount} 项（已释放 ${blobUrlsToRevoke.length} 个 Blob URL）`);
                  resolve();
                }
              };
            });
          };
          
          request.onerror = () => {
            console.warn(`🖼️ [图集媒体缓存] 清除缓存失败`);
            resolve();
          };
        } catch (error) {
          console.warn(`🖼️ [图集媒体缓存] 创建清除事务失败`, error);
          resolve();
        }
      });
    } catch (error) {
      console.error('❌ [图集媒体缓存] 清除缓存失败:', error);
    }
  }
  
  /**
   * 丢弃某一项的内存 Blob URL 与 IndexedDB 记录，便于回退 HTTPS 直链重下
   */
  async invalidateCachedMedia(userId, songId, index) {
    const cacheKey = this.generateCacheKey(userId, songId, index);
    const existing = this.blobUrlCache.get(cacheKey);
    if (existing) {
      try {
        URL.revokeObjectURL(existing);
      } catch {
        /* ignore */
      }
      this.blobUrlCache.delete(cacheKey);
    }
    this.inFlightUrlRequests.delete(cacheKey);

    try {
      await this.ensureDBReady();
      await new Promise((resolve) => {
        try {
          const tx = this.db.transaction([ALBUM_MEDIA_STORE], 'readwrite');
          tx.objectStore(ALBUM_MEDIA_STORE).delete(cacheKey);
          tx.oncomplete = () => resolve();
          tx.onerror = () => resolve();
        } catch {
          resolve();
        }
      });
      console.log(`🖼️ [图集媒体缓存] 已失效缓存: ${cacheKey}`);
    } catch (error) {
      console.warn(`🖼️ [图集媒体缓存] 失效缓存失败: ${cacheKey}`, error);
    }
  }

  /**
   * 🔥 新增：清理所有 Blob URL（用于策略切换时清理）
   * @param {string|number} userId - 用户ID
   * @param {string|number} songId - 歌曲ID
   */
  clearBlobUrls(userId, songId) {
    const prefix = `${userId}_${songId}_album-media`;
    const blobUrlsToRevoke = [];
    
    this.blobUrlCache.forEach((blobUrl, cacheKey) => {
      if (cacheKey.startsWith(prefix)) {
        blobUrlsToRevoke.push(blobUrl);
        this.blobUrlCache.delete(cacheKey);
      }
    });
    
    blobUrlsToRevoke.forEach(blobUrl => {
      try {
        URL.revokeObjectURL(blobUrl);
        console.log(`🧹 [图集媒体缓存] 清理 Blob URL: ${blobUrl.substring(0, 50)}...`);
      } catch (error) {
        console.warn(`⚠️ [图集媒体缓存] 清理 Blob URL 失败:`, error);
      }
    });
    
    if (blobUrlsToRevoke.length > 0) {
      console.log(`🧹 [图集媒体缓存] 已清理 ${blobUrlsToRevoke.length} 个 Blob URL`);
    }
  }
}

// 导出单例
const albumMediaCacheService = new AlbumMediaCacheService();
export default albumMediaCacheService;
